import React from "react";
import { Button, Col, Form, Input, Modal, Row, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";

import APIService from "../../Service/APIService.js";
import { colors } from "../../theme/colors.js";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  role: "Admin",
  region: null,
};

const ASUserAddAction = ({ canAddUser, onUserAdded }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [formValues, setFormValues] = React.useState(emptyForm);
  const [regions, setRegions] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  if (!canAddUser) {
    return null;
  }

  const updateFormValue = (field, value) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormValues(emptyForm);
  };

  const handleOpen = async () => {
    setIsOpen(true);

    if (regions.length > 0) {
      return;
    }

    try {
      const apiService = new APIService();
      const response = await apiService.getDataFromBackend("getallregions");
      setRegions(
        response?.data?.data?.map((region) => ({
          value: region.ID,
          label: region.Region,
        })) ?? []
      );
    } catch {
      setRegions([]);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    resetForm();
  };

  const registerUser = async () => {
    const firstName = formValues.firstName.trim();
    const lastName = formValues.lastName.trim();
    const email = formValues.email.trim();
    const phone = formValues.phone.trim();

    if (!firstName || !lastName || !email || !phone || !formValues.role) {
      Swal.fire({
        title: "Error!",
        text: "Please fill all the fields",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (formValues.role === "ASD" && !formValues.region) {
      Swal.fire({
        title: "Error!",
        text: "Please select a region",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to add this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) {
      return;
    }

    const postObj = {
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      Phone: phone,
      RoleType: formValues.role,
      Username: `${firstName}${lastName}`,
      Type: formValues.role,
      RegionID: formValues.region?.value,
      RegionName: formValues.region?.label,
    };

    setSubmitting(true);
    try {
      const apiService = new APIService();
      const response = await apiService.addDataToBackend(postObj, "register");

      if (response?.status === 200 || response?.status === 201) {
        setIsOpen(false);
        resetForm();
        await Swal.fire({
          title: "Success!",
          text: "User Added Successfully",
          icon: "success",
          confirmButtonText: "Ok",
        });
        await onUserAdded?.();
        return;
      }

      Swal.fire({
        title: "Error!",
        text: "Error while adding user",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Error while adding user",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        type="primary"
        icon={<PlusOutlined />}
        style={{ background: colors["primaryColor"], height: "36px" }}
      >
        Add AS User
      </Button>

      <Modal
        title="Add New AS User"
        open={isOpen}
        onCancel={handleCancel}
        footer={null}
        centered
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="First Name">
                <Input
                  placeholder="Enter First Name"
                  value={formValues.firstName}
                  onChange={(event) =>
                    updateFormValue("firstName", event.target.value)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Last Name">
                <Input
                  placeholder="Enter Last Name"
                  value={formValues.lastName}
                  onChange={(event) =>
                    updateFormValue("lastName", event.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Email">
                <Input
                  placeholder="Email"
                  value={formValues.email}
                  onChange={(event) =>
                    updateFormValue("email", event.target.value)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Phone">
                <Input
                  placeholder="Enter Phone"
                  value={formValues.phone}
                  onChange={(event) =>
                    updateFormValue("phone", event.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Role">
                <Select
                  value={formValues.role}
                  options={[
                    { value: "Admin", label: "Admin" },
                    { value: "ASD", label: "ASD" },
                    { value: "Authorizer", label: "Authorizer" },
                    { value: "Viewer", label: "Viewer" },
                  ]}
                  onChange={(role) => {
                    updateFormValue("role", role);
                    if (role !== "ASD") {
                      updateFormValue("region", null);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {formValues.role === "ASD" && (
              <Col span={12}>
                <Form.Item label="Region">
                  <Select
                    value={formValues.region?.value}
                    options={regions}
                    onChange={(_value, option) =>
                      updateFormValue("region", option)
                    }
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <p>
            Adding and activating an AS contact gives them access to Tissue
            Vault.
          </p>
          <Button
            type="primary"
            style={{ background: "#27a907" }}
            onClick={registerUser}
            loading={submitting}
          >
            Add and Activate
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default ASUserAddAction;
